import { ReactNode, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useRole } from '../../context/RoleContext';
import { useTheme } from '../../context/ThemeContext';
import { useAccent } from '../../context/AccentContext';
import { NotificationBell } from 'notification-kit/client/index.js';
import type { Page, ParticipantTab } from '../../App';
import { api } from '../../api/client';
import { useT } from '../../i18n';
import { ProfileMenu } from '../ProfileMenu';

const PAGE_TITLE_KEYS: Record<Page, string> = {
  portfolio: 'nav.portfolio',
  participant: 'nav.participant',
  admin: 'nav.admin',
  profile: 'nav.profile',
  equipe: 'nav.equipe',
  conseillers: 'nav.conseillers',
  notifications: 'nav.notifications',
  communications: 'nav.communications',
};

interface AppShellProps {
  children: ReactNode;
  currentPage: Page;
  onNavigate: (page: Page) => void;
  /** Open participant drilldowns, rendered as tabs nested under Portfolio. */
  participantTabs: ParticipantTab[];
  activeParticipantId: number | null;
  onSelectParticipantTab: (tab: ParticipantTab) => void;
  onCloseParticipantTab: (id: number) => void;
}

export function AppShell({ children, currentPage, onNavigate, participantTabs, activeParticipantId, onSelectParticipantTab, onCloseParticipantTab }: AppShellProps) {
  const { t } = useT();
  const { user, impersonatedBy, stopImpersonating } = useAuth();
  const {
    isAdmin,
    isResponsable,
    role,
    activeLens,
    availableLenses,
    hasLensChoice,
    setLens,
  } = useRole();
  // 'rm' / 'rp' lenses are portfolio scopes — equipe/conseillers must hide.
  const inPortfolioLens = activeLens === 'rm' || activeLens === 'rp';
  const { theme, toggleTheme } = useTheme();
  const { logoUrl } = useAccent();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }, [collapsed]);

  const roleLabel = (() => {
    if (role === 'admin') return t('role.admin');
    if (role === 'resp_oai') return t('role.respOai');
    if (role === 'resp_ri') return t('role.respRi');
    if (role === 'resp_all') return t('role.respAll');
    return t('role.rm');
  })();

  const roleBadge = () => {
    if (hasLensChoice) return null; // Lens dropdown renders the role label itself.
    if (isAdmin) return null;
    if (role === 'resp_all' || role === 'resp_oai' || role === 'resp_ri') {
      return <span className="badge badge-direction" title={roleLabel}>{roleLabel}</span>;
    }
    return <span className="badge" title={t('shell.user')}>RM</span>;
  };

  const lensLabel = (l: typeof activeLens): string => {
    switch (l) {
      case 'rm':   return t('lens.rm');
      case 'rp':   return t('lens.rp');
      case 'oai':  return t('lens.oai');
      case 'ri':   return t('lens.ri');
      case 'all':  return t('lens.all');
      case 'auto': return t('lens.auto');
    }
  };

  // On a participant tab, the topbar names the participant instead of the
  // generic page title.
  const activeParticipantTab = currentPage === 'participant'
    ? participantTabs.find(tab => tab.id === activeParticipantId)
    : undefined;
  const pageTitle = activeParticipantTab?.name || t(PAGE_TITLE_KEYS[currentPage]);

  // Sidebar badges (unsent comms + open alerts). Refreshes on mount,
  // on page change, and when the window regains focus — cheap queries.
  const [unsentComms, setUnsentComms] = useState<number | null>(null);
  const [openAlerts, setOpenAlerts] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = () => {
      (api.getAllCommunications({ status: 'unsent', limit: 1 }) as Promise<any>)
        .then((data) => { if (!cancelled) setUnsentComms(data?.counts?.unsent ?? 0); })
        .catch(() => { if (!cancelled) setUnsentComms(0); });
      (api.getAlerts({ status: 'open', limit: 1 }) as Promise<any>)
        .then((data) => { if (!cancelled) setOpenAlerts(data?.counts?.open ?? 0); })
        .catch(() => { if (!cancelled) setOpenAlerts(0); });
    };
    fetchCounts();
    const onFocus = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, [currentPage, activeLens]);

  const navItem = (page: Page, label: string, badge?: number | null) => (
    <a
      className={`nav-link${currentPage === page ? ' active' : ''}`}
      href={`#${page}`}
      onClick={(e) => { e.preventDefault(); onNavigate(page); }}
    >
      {label}
      {badge != null && badge > 0 && (
        <span
          style={{
            marginLeft: 6,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 18,
            height: 18,
            padding: '0 5px',
            borderRadius: 9,
            background: 'var(--accent)',
            color: 'var(--surface)',
            fontSize: 10,
            fontWeight: 700,
            verticalAlign: 'middle',
          }}
        >
          {badge}
        </span>
      )}
    </a>
  );

  // Participant drilldown tab, indented under Portfolio. The family name
  // ellipsizes when it doesn't fit the sidebar (full name on hover via title).
  const participantTabItem = (tab: ParticipantTab) => {
    const isActive = currentPage === 'participant' && activeParticipantId === tab.id;
    return (
      <div
        key={tab.id}
        className={`nav-link participant-tab${isActive ? ' active' : ''}`}
        title={tab.name ?? t('nav.participant')}
        onClick={() => onSelectParticipantTab(tab)}
      >
        <span className="participant-tab__label">{tab.name ?? '…'}</span>
        <button
          type="button"
          className="participant-tab__close"
          title={t('shell.closeTab')}
          aria-label={t('shell.closeTab')}
          onClick={(e) => { e.stopPropagation(); onCloseParticipantTab(tab.id); }}
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <>
      <header className="topbar">
        <div className="topbar-left">
          <div className="brand">
            {logoUrl && (
              <img src={logoUrl} alt="" className="brand-logo" onClick={() => onNavigate('portfolio')} />
            )}
            <span className="brand-mark" onClick={() => onNavigate('portfolio')} style={{ cursor: 'pointer' }}>{t('brand.name')}</span>
            <span className="brand-sep" />
            <span className="brand-page">{pageTitle}</span>
          </div>
        </div>
        <div className="topbar-right">
          <NotificationBell onNavigate={(hash) => { window.location.hash = hash; }} />
          {hasLensChoice && (
            <div className="lens-picker">
              <span
                className={`lens-picker__label${isAdmin ? ' lens-picker__label--clickable' : ''}`}
                onClick={isAdmin ? () => onNavigate('admin') : undefined}
                title={isAdmin ? t('shell.openAdmin') : roleLabel}
              >
                {roleLabel}
              </span>
              <select
                className="lens-picker__select"
                value={activeLens}
                onChange={(e) => setLens(e.target.value as typeof activeLens)}
                aria-label={t('shell.chooseView')}
                title={t('shell.chooseView')}
              >
                {availableLenses.filter((l) => l !== 'auto').map((l) => (
                  <option key={l} value={l}>{lensLabel(l)}</option>
                ))}
              </select>
            </div>
          )}
          <button className="theme-btn" onClick={toggleTheme} title={theme === 'light' ? t('shell.themeSwitchToDark') : t('shell.themeSwitchToLight')}>
            {theme === 'light' ? '☾' : '☀'}
          </button>
          {isAdmin && !hasLensChoice && (
            <span
              className="badge badge-accent badge-clickable"
              onClick={() => onNavigate('admin')}
              title={t('nav.admin')}
            >
              {t('nav.admin')}
            </span>
          )}
          {roleBadge()}
          <ProfileMenu onNavigateProfile={() => onNavigate('profile')} />
        </div>
      </header>

      <div className={`shell ${collapsed ? 'shell--collapsed' : ''}`}>
        <aside className="sidebar">
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? '›' : `‹ ${t('shell.collapse')}`}
          </button>

          {!collapsed && (
            <nav className="sidebar-nav">
              <div className="nav-section">
                {navItem('portfolio', t('nav.portfolio'))}
                {participantTabs.map(participantTabItem)}
                {isResponsable && !inPortfolioLens && navItem('equipe', t('nav.equipe'))}
                {isResponsable && !inPortfolioLens && navItem('conseillers', t('nav.conseillers'))}
                {navItem('notifications', t('nav.notifications'), openAlerts)}
                {navItem('communications', t('nav.communications'), unsentComms)}
              </div>
            </nav>
          )}
        </aside>

        <main className="content">
          {impersonatedBy && (
            <div className="impersonation-banner">
              <span>{t('shell.impersonating')} <strong>{user?.name}</strong> — {t('shell.impersonatingBy')} {impersonatedBy.name}</span>
              <button className="btn btn-sm" onClick={() => { stopImpersonating(); window.location.reload(); }}>
                {t('shell.stopImpersonating')}
              </button>
            </div>
          )}
          <div className="page" key={activeLens}>{children}</div>
        </main>
      </div>
    </>
  );
}
